import { z } from 'zod';
import { idSchema, isoDateSchema, memberSummarySchema } from './common';
import { MAX_AROMA_TAGS } from '../aroma-tags';
import { SCORE_MAX, SCORE_MIN } from '../score';
const criterionSchema = z
    .number()
    .int('Une note se saisit au dixième.')
    .min(SCORE_MIN)
    .max(SCORE_MAX);
/**
 * Les descripteurs d'une note.
 *
 * Ce schéma validait autrefois l'appartenance à une liste compilée dans le bundle. Depuis
 * que le référentiel vit en base et que tout membre peut l'enrichir, il ne peut plus :
 * une constante figée refuserait le terme que le membre vient tout juste de créer.
 *
 * Il ne dit donc plus que la **forme** — au plus huit, pas de doublon, pas de chaîne
 * vide. L'**existence** est vérifiée côté API, par une règle de domaine qui interroge le
 * référentiel (`aroma-tags-must-exist.rule.ts`). Le front la vérifie de son côté contre
 * la liste qu'il garde en cache, ce qui lui permet de rester utilisable hors-ligne.
 */
const aromaTagsSchema = z
    .array(z.string().trim().min(1, 'Un descripteur ne peut pas être vide.'))
    .max(MAX_AROMA_TAGS, `Au plus ${MAX_AROMA_TAGS} descripteurs.`)
    .refine((tags) => new Set(tags).size === tags.length, {
    message: 'Descripteur en double.',
})
    .default([]);
/**
 * Écran 04 — saisie ou modification d'une note.
 *
 * `overall` n'est pas dans le payload : il est dérivé côté domaine de nez/bouche/finale.
 * `clientUpdatedAt` porte l'horodatage de la saisie sur l'appareil ; c'est lui qui arbitre
 * le last-write-wins quand une note rejouée depuis la file offline arrive après coup.
 */
export const upsertTastingSchema = z.object({
    sessionId: idSchema,
    rumId: idSchema,
    nose: criterionSchema,
    palate: criterionSchema,
    finish: criterionSchema,
    comment: z.string().trim().max(1000).nullish(),
    aromaTags: aromaTagsSchema,
    /** true = « Valider ma note » : geste irréversible qui ouvre l'accès aux notes des autres. */
    validate: z.boolean().default(false),
    clientUpdatedAt: isoDateSchema,
});
export const tastingViewSchema = z.object({
    id: idSchema,
    sessionId: idSchema,
    rumId: idSchema,
    member: memberSummarySchema,
    nose: z.number().int(),
    palate: z.number().int(),
    finish: z.number().int(),
    overall: z.number().int(),
    comment: z.string().nullable(),
    aromaTags: z.array(z.string()),
    validatedAt: isoDateSchema.nullable(),
    updatedAt: isoDateSchema,
});
/**
 * Réponse de `GET /api/rums/:id/tastings`.
 *
 * Tant que le membre courant n'a pas validé sa propre note, l'API répond 403 (règle anti-biais,
 * US-09) : ce corps n'existe simplement pas. Il n'y a pas de variante « partiellement masquée »,
 * ce qui évite qu'un oubli d'affichage laisse fuir une moyenne.
 */
export const rumTastingsSchema = z.object({
    rumId: idSchema,
    sessionId: idSchema,
    average: z.number().int().nullable(),
    averageNose: z.number().int().nullable(),
    averagePalate: z.number().int().nullable(),
    averageFinish: z.number().int().nullable(),
    tastings: z.array(tastingViewSchema),
});
/** Corps du 409 renvoyé quand le serveur détient une version plus récente (last-write-wins). */
export const tastingConflictSchema = z.object({
    reason: z.literal('stale-write'),
    current: tastingViewSchema,
});
//# sourceMappingURL=tasting.js.map